
import { useWorkoutPlanRoutines } from "../hooks/useWorkoutPlanRoutines";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "@/hooks/use-toast";
import { Plus } from "lucide-react";
import { useFilteredRoutines } from "../hooks/useFilteredRoutines";
import { useNavigate } from "react-router-dom";

interface AvailableRoutinesListProps {
  planId: string;
  onRoutineSelect: (routineId: string, preloadedRoutine?: any) => void;
}

export const AvailableRoutinesList = ({
  planId,
  onRoutineSelect,
}: AvailableRoutinesListProps) => {
  const { data: routines, isLoading: isLoadingPlanRoutines } = useWorkoutPlanRoutines(planId);
  const { data: filteredRoutines, isLoading: isLoadingFilteredRoutines } = useFilteredRoutines(planId);
  const [newRoutineName, setNewRoutineName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { profile } = useProfile();
  const navigate = useNavigate();
  
  // Combined loading state
  const isLoading = isLoadingPlanRoutines || isLoadingFilteredRoutines;

  const handleCreateRoutine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    
    if (!newRoutineName.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El nombre de la rutina es requerido",
      });
      return;
    }
    
    setIsCreating(true);
    try {
      // Create the routine
      const { data: routine, error: routineError } = await supabase
        .from("routines")
        .insert({
          name: newRoutineName,
          user_id: profile.id
        })
        .select()
        .single();

      if (routineError) throw routineError;

      // Pass the new routine ID to the parent handler
      if (routine) {
        await onRoutineSelect(routine.id);
      }

      toast({
        title: "Rutina creada",
        description: "La rutina se ha creado y agregado al plan correctamente",
        variant: "success"
      });

      setNewRoutineName("");
      
      // Reload the page to show the changes after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500); // 1.5 second delay to allow the toast to be visible
      
    } catch (error: any) {
      console.error("Error creating routine:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo crear la rutina",
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Cargando rutinas...</div>;
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleCreateRoutine} className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Nombre de la nueva rutina"
            value={newRoutineName}
            onChange={(e) => setNewRoutineName(e.target.value)}
            className="bg-white/5"
          />
          <Button 
            type="submit" 
            disabled={isCreating || !newRoutineName.trim()}
            className="whitespace-nowrap bg-[#2196A3] sunset:bg-sunset forest:bg-forest ocean:bg-ocean hover:bg-[#00C1D6]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear
          </Button>
        </div>
      </form>

      {filteredRoutines && filteredRoutines.length > 0 ? (
        <>
          <h3 className="text-sm font-semibold text-white/80 ocean:text-[#06A2D6]">Rutinas disponibles</h3>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {filteredRoutines.map((routine) => (
                <Button
                  key={routine.id}
                  variant="outline"
                  className="w-full justify-start hover:bg-[#2D6970]/20 ocean:hover:text-white"
                  onClick={() => onRoutineSelect(routine.id, routine)}
                >
                  {routine.name}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </>
      ) : (
        <div className="text-center py-4 text-white/70">
          No hay rutinas disponibles para añadir. Puedes copiar rutinas desde la sección "Buscar Planes".
        </div>
      )}
    </div>
  );
};
